import React from 'react';
import { ExternalLink } from '../../../components';
import { FormattedMessage } from '../../../util/reactIntl';
import { useHistory } from 'react-router-dom/cjs/react-router-dom.min';
import css from '../TermsOfServicePage.module.css';

function ConsumerTermsEnglish() {
  const history = useHistory();
  return (
    <div className="terms-page">
      <h1>Terms of Use for Consumers</h1>

      <h2>DOCUMENT INFORMATION</h2>
      <p>
        Applies to: Consumers (Users) seeking or booking services via the Platform
        <br />
        Last amended: 5/12/2025
        <br />
        Related documents:{' '}
        <span
          className={css.termsLink}
          role="button"
          onClick={() => history.push('/terms-of-service?tab=professional')}
        >
          Terms of Use for Professional Users
        </span>{' '}
        | <ExternalLink href="/privacy-policy">Privacy Policy</ExternalLink>
      </p>

      <h2>Preamble</h2>
      <p>
        These Terms of Use apply exclusively to Consumers (also referred to as “Users”) who register
        and use the website located at www.vivianlab.gr, www.vivianlab.com, and
        www.vivianlab.health, and related mobile applications (collectively, the "Platform") to
        seek, book, or receive services.
      </p>
      <p>
        The Platform is owned and operated by the company under the name “VIVIANLAB LTD" and the
        distinctive title “Vivian Lab", with registered office at 1612 SOFIA BUL. TSAR BORIS III № 81
        ET.1, AP.1, VAT BG207933919, electronic contact info@vivianlab.com (the “Company”).
      </p>
      <p>
        All content, trademarks, and domain names (including www.vivianlab.gr, www.vivianlab.com,
        and www.vivianlab.health) are the exclusive property of the Company and are protected by
        copyright, trademark, and other applicable intellectual property laws.
      </p>
      <p>
        The Company is committed to ensuring that the Platform is accessible to all users, including
        those with disabilities, in compliance with the European Accessibility Act (Directive (EU)
        2019/882) and relevant technical standards.
      </p>
      <p>
        Medical & AI Disclaimer: The Platform offers tools that may provide wellness insights,
        symptom tracking, or AI-assisted guidance. These tools do not constitute medical advice,
        diagnosis, or treatment, and are not a substitute for consultation with a licensed
        healthcare professional. The Platform is not intended for medical emergencies or urgent
        health situations. If you experience severe symptoms, you must contact a doctor or emergency
        services immediately.
      </p>
      <p>
        Global Regulatory Disclaimer (EU & U.S.): The Platform and its services are intended for general wellness and informational purposes only and are not classified as a medical device under Regulation (EU) 2017/745 nor regulated by the U.S. Food and Drug Administration (FDA). The Platform does not provide medical advice, diagnosis, or treatment under any jurisdiction.
      </p>
      <p>
        AI System Transparency & Human Oversight: The AI features available on the Platform operate without human supervision in real time and do not involve human review of individual outputs. The system generates responses based on patterns in data and may produce results that are inaccurate, incomplete, or misleading. Users must independently verify all information before acting on it.
      </p>

      <h2>1. DEFINITIONS</h2>
      <p>For purposes of these Terms of Use:</p>
      <ul>
        <li>
          <strong>“Consumer” or “User”:</strong> Any individual using the Platform to seek, book, or
          receive services or digital content from Professional Users.
        </li>
        <li>
          <strong>“Professional User” or “Provider”:</strong> Any individual or legal entity
          registered on the Platform to offer professional services (including health specialists,
          consultants, and businesses active in the health and wellness sector). The Company does
          not verify or guarantee the medical licensing, competence, or regulatory status of
          Professional Users unless expressly stated.
        </li>
        <li>
          <strong>“Platform” or “Marketplace”:</strong> The online environment operated by under the
          brand name VivianLab, accessible via website and mobile applications.
        </li>
        <li>
          <strong>“Company”:</strong> , the legal entity that owns and operates the Platform.
        </li>
        <li>
          <strong>“Services”:</strong> All intermediary services provided by the Company through the Platform, including professional profile display, appointment booking, video consultations, educational content, and AI-assisted functionalities.
        </li>
        <li>
          <strong>“AI Tools”:</strong> Automated digital tools made available through the Platform that generate wellness-related suggestions, educational content, or symptom insights based on user-provided data. AI Tools are informational only and do not provide medical diagnosis or clinical decision-making.
        </li>
        <li>
          <strong>“AI System”:</strong> Any software system developed or integrated by the Company that generates outputs such as predictions, recommendations, or content influencing user decisions, within the meaning of the EU Artificial Intelligence Act.
        </li>
        <li>
          <strong>“Agreement”:</strong> These Terms of Use, together with the{' '}
          <ExternalLink href="/privacy-policy">Privacy Policy</ExternalLink>.
        </li>
        <li>
          <strong>“Business Days”:</strong> Any day other than Saturday, Sunday, or public holiday
          in Greece.
        </li>
        <li>
          <strong>“Session” or “Appointment”:</strong> A scheduled consultation or service delivery
          between a Consumer and a Professional User via the Platform.
        </li>
        <li>
          <strong>“Subscription”:</strong> Paid access to premium features and content on the
          Platform (monthly or annual).
        </li>
      </ul>

      <h2>2. ACCEPTANCE OF TERMS</h2>
      <p>
        By registering on or using the Platform, you (the Consumer) fully and unconditionally accept
        and agree to be bound by:
      </p>
      <ol>
        <li>These Terms of Use for Consumers;</li>
        <li>
          The <ExternalLink href="/privacy-policy">Privacy Policy</ExternalLink>; and
        </li>
        <li>
          The <ExternalLink href="/cookie-policy">Cookie Policy</ExternalLink>.
        </li>
        <li>
          By using AI features on the Platform, you acknowledge that responses may be generated by
          automated systems and may occasionally be inaccurate or incomplete. You agree not to rely
          on AI content for medical decision-making.
        </li>
        <li>
          You acknowledge that AI outputs are generated automatically without human intervention and may not reflect accurate, complete, or up-to-date information. You assume full responsibility for any decisions or actions taken based on AI-generated content.
        </li>
      </ol>
      <p>If you do not accept these terms, you must not register or use the Platform.</p>

      <h2>3. DATA PROTECTION</h2>
      <p>
        The Company acts as Data Controller for personal data you provide directly to the Company
        for purposes such as:
      </p>
      <ul>
        <li>Creating and managing your Consumer account</li>
        <li>Processing subscription and appointment payments</li>
        <li>Sending newsletters and marketing communications (with your consent)</li>
        <li>Platform administration and security</li>
      </ul>
      <p>
        When you book services with a Professional User, the Professional User acts as the Data
        Controller for personal data related to that service or consultation. The Company may
        process this data on behalf of the Professional User as a Data Processor. The Platform
        processes data relating to menopause, hormonal health, symptoms, and wellbeing, which may
        constitute special category data under Article 9 GDPR. Such data is processed only with your
        explicit consent, solely for the purposes of providing the Platform’s services, AI tools,
        and personalized wellness insights.
      </p>
      <p>
        AI outputs may be based on aggregated or pseudonymized data. The Company does not use your
        health data for automated decision-making producing legal effects.
      </p>
      <p>
        All information regarding purposes, legal bases for processing, retention periods, data
        recipients, and your rights as a data subject is detailed in our{' '}
        <ExternalLink href="/privacy-policy">Privacy Policy</ExternalLink>.
      </p>
      <p>
        <strong>AI Processing Transparency:</strong> The Company uses AI systems to process user-provided data (including health-related inputs where applicable) to generate wellness insights and recommendations. This processing does not involve profiling that produces legal or similarly significant effects under Article 22 GDPR. AI outputs are not used to make decisions affecting your legal rights, access to services, or financial status.
      </p>
      <p>
        <strong>International Data Protection Compliance:</strong> Where the Platform is accessed by users outside the European Economic Area, including the United States, the Company applies data protection standards aligned with applicable laws, including GDPR and relevant U.S. state privacy laws, where applicable.
      </p>

      <h3>
        <FormattedMessage id="ProfileImagesSection.titleWithNumber" values={{ number: '3.1' }} />
      </h3>
      <ul>
        <li>
          <FormattedMessage id="ProfileImagesSection.cameraAccess" />
        </li>
        <li>
          <FormattedMessage id="ProfileImagesSection.usage" />
        </li>
        <li>
          <FormattedMessage id="ProfileImagesSection.storage" />
        </li>
      </ul>

      <h2>4. ONLINE SERVICES PROVIDED BY THE PLATFORM</h2>
      <p>
        The Platform is an intermediary marketplace specialising in health, wellness, and related
        consulting services. The Platform does not itself provide medical, health, or professional
        services; it provides only the technical means for Consumers to connect with and receive
        services from Professional Users. No Medical Device: The Platform, including its AI Tools,
        is not classified as a medical device under the EU Medical Device Regulation (EU) 2017/745
        and must not be used for diagnosis, treatment, or clinical risk assessment.
      </p>
      <p>Services available through the Platform include:</p>
      <h3>4.1 Free Services</h3>
      <ul>
        <li>
          Browsing professional profiles (contact details, qualifications, specialisations, services
          offered, pricing, working hours)
        </li>
        <li>Accessing educational content (articles, videos, recipes)</li>
        <li>
          Using AI-assisted tools (symptom tracking, personalised guidance). AI-assisted tools may
          generate wellness suggestions based on user input. These suggestions are general,
          non-clinical, may contain inaccuracies, and must not replace consultation with qualified
          health professionals.
        </li>
        <li>Participating in free group webinars and hotlines</li>
        <li>
          AI tools are non-deterministic systems and may generate different outputs for the same input. The Company does not guarantee consistency, accuracy, or reliability of AI-generated content. AI-generated outputs may vary and should not be interpreted as professional, medical, or regulated advice under any jurisdiction.
        </li>
      </ul>
      <h3>4.2 Subscription Services</h3>
      <p>
        Access to certain premium features and content requires a paid subscription.
      </p>
      <p>It costs 45€ for 1 year or 4.99€ for 1 month.</p>
      <p>Subscription features include:</p>
      <ul>
        <li>Access to exclusive articles, videos, and recipes</li>
        <li>Participation in premium webinars and group sessions</li>
        <li>Enhanced AI tools and personalized content</li>
      </ul>
      <p>Subscription terms:</p>
      <ul>
        <li>
          Free Trial: Your subscription begins with a 14-day free trial. Upon the expiration of
          the trial, you will be automatically charged the full subscription fee unless you cancel
          at least 24 hours before the trial ends.
        </li>
        <li>
          Automatic Renewal: Subscriptions automatically renew unless cancelled at least 24 hours
          before the renewal date.
        </li>
        <li>Refund Policy: Subscription fees are non-refundable.</li>
        <li>
          Cancellation: You may cancel your subscription at any time via your account settings or
          by contacting the company.
        </li>
      </ul>
      <h3>4.3 Individual Sessions</h3>
      <p>Consumers may book individual consultations with Professional Users. These sessions:</p>
      <ul>
        <li>Are charged separately from the subscription fee</li>
        <li>Are priced by the Professional User (not by the Company)</li>
        <li>
          Take place via video call (Google Meet or the Platform’s own system) or chat
        </li>
      </ul>
      <p>
        The Company undertakes to ensure that the Platform and the means for providing online
        services conform to the contract and are fit for the purposes for which digital intermediary
        services would normally be used, with the professional diligence and skill expected.
      </p>
      <h3>4.4 AI SYSTEM LIMITATIONS AND USER RESPONSIBILITY</h3>
      <p>The AI features provided through the Platform are designed for general wellness and informational purposes only. They are not intended to replace professional medical judgment, diagnosis, or treatment. Users acknowledge that:</p>
      <ul>
        <li>AI outputs may be inaccurate, incomplete, biased, or outdated</li>
        <li>AI does not understand individual medical history unless explicitly provided</li>
        <li>AI cannot assess emergencies or critical health conditions</li>
      </ul>
      <p>
        Users remain solely responsible for evaluating AI outputs and must consult qualified professionals before making health-related decisions.
      </p>

      <h2>5. CONSUMER OBLIGATIONS</h2>
      <h3>5.1 Registration and Account Requirements</h3>
      <p>Consumers must:</p>
      <ul>
        <li>
          Provide accurate, complete, and up-to-date information during registration, including:
          <ul>
            <li>Full name</li>
            <li>Email address</li>
            <li>Contact information</li>
            <li>Payment details (for subscriptions and appointments)</li>
          </ul>
        </li>
        <li>Update their profile promptly when information changes</li>
      </ul>
      <p>
        The Consumer is solely responsible for submitting true and accurate information. The
        Consumer is liable for any damages the Company may suffer due to violation of this
        obligation.
      </p>
      <h3>5.2 Account Security</h3>
      <p>
        Consumers are responsible for maintaining the confidentiality and security of their account
        credentials, including passwords and authentication information. Consumers must choose
        strong, unique passwords and take reasonable steps to protect their accounts from
        unauthorised access. The Company is not liable for any loss or damage resulting from a
        Consumer's failure to comply with these account security obligations.
      </p>
      <h3>5.3 Lawful Use</h3>
      <p>Consumers must:</p>
      <ul>
        <li>Use the Platform lawfully and in accordance with these Terms of Use</li>
        <li>Not engage in fraudulent activity or misuse the Platform</li>
        <li>Respect the rights of Professional Users and other Consumers</li>
        <li>Act in good faith and with respect</li>
        <li>
          Consumers must not rely on AI-generated information for medical decisions or emergencies.
          Consumers must use the Platform responsibly and consult licensed professionals for medical
          guidance.
        </li>
        <li>
          Users must not attempt to manipulate, exploit, or test the AI system in ways that may produce harmful, misleading, or unsafe outputs.
        </li>
      </ul>

      <h2>6. BOOKING AND PAYMENT TERMS</h2>
      <h3>6.1 Appointment Booking</h3>
      <p>To book an individual session with a Professional User:</p>
      <ol>
        <li>Browse Professional User profiles</li>
        <li>Select a Professional User and available appointment slot</li>
        <li>Provide necessary information (name, contact details, reason for consultation)</li>
        <li>Confirm booking and payment method</li>
      </ol>
      <p>Upon booking, you will receive a confirmation email with appointment details.</p>
      <h3>6.2 Payment Processing</h3>
      <p>
        Payments are processed through Stripe, a third-party payment processor. By registering
        payment details, you accept Stripe's Terms and Conditions.
      </p>
      <p>
        The Company does not directly handle payments and is not responsible for payment processing
        issues. In the case of disputes relating to Professional User services, the Company acts
        only as an intermediary and is not responsible for the quality, suitability, or outcome of
        such services.
      </p>
      <p>Payment process:</p>
      <ol>
        <li>When you book an appointment, your credit card is pre-authorized (not charged).</li>
        <li>After the session, the Professional User confirms completion.</li>
        <li>
          Upon confirmation, your card is charged, and funds are transferred to the Professional
          User.
        </li>
      </ol>
      <p>
        If the Professional User does not confirm payment within 7 days, the pre-authorisation will
        be released, and no charge will occur.
      </p>
      <h3>6.3 Pricing</h3>
      <ul>
        <li>Subscription fees are set by the Company and displayed on the Platform</li>
        <li>
          Individual session fees are set by the Professional User and displayed on their profile
        </li>
      </ul>
      <p>The Company does not control or verify Professional User pricing.</p>
      <h3>6.4 Receipts and Invoices</h3>
      <ul>
        <li>For subscriptions: The Company will issue a receipt or invoice</li>
        <li>
          For individual sessions: The Professional User is responsible for issuing a receipt or
          invoice
        </li>
      </ul>
      <p>
        Professional Users are required to issue proper tax documents (receipts or invoices) for
        individual sessions in accordance with applicable tax and accounting laws. The Company bears
        no responsibility for any tax, licensing, or regulatory compliance obligations of
        Professional Users.
      </p>
      <h3>6.5 Vouchers and Promotional Codes</h3>
      <p>
        The Company may offer vouchers or promotional codes that reduce or eliminate the cost of
        sessions or subscriptions. Vouchers and promotional codes:
      </p>
      <ul>
        <li>Are subject to specific terms and conditions (displayed at the time of issuance)</li>
        <li>May have expiration dates or usage limits</li>
        <li>Cannot be combined with other offers unless explicitly stated</li>
        <li>Are non-transferable and have no cash value</li>
      </ul>

      <h2>7. RIGHT OF WITHDRAWAL</h2>
      <p>
        For services that begin within the 14-day withdrawal period (e.g., an appointment or access
        to digital premium content), you agree that by starting the service you expressly waive your
        right of withdrawal, as required by EU consumer protection law. As a Consumer, you have the
        right to withdraw from a scheduled appointment or subscription within 14 calendar days from
        the date of booking or purchase, unless:
      </p>
      <ul>
        <li>The service has already been performed; or</li>
        <li>
          You expressly consent to commencement of service during the withdrawal period and
          acknowledge loss of the right of withdrawal.
        </li>
      </ul>
      <h3>7.1 Withdrawal from Appointments</h3>
      <p>Cancellation policy:</p>
      <ul>
        <li>More than 24 hours before appointment: Full refund</li>
        <li>
          Less than 24 hours but more than 60 minutes before appointment: Cancellation fees may
          apply
        </li>
        <li>Less than 60 minutes before appointment: No refund</li>
      </ul>
      <p>
        To cancel an appointment, contact the Company at or use the cancellation feature in your
        account.
      </p>
      <p>
        Refunds may take up to 14 business days to be returned to your account due to processing
        requirements.
      </p>
      <h3>7.2 Withdrawal from Digital Content</h3>
      <p>
        For downloadable digital content (e.g., articles, eBooks, videos): By downloading, you
        expressly waive the right of withdrawal.
      </p>
      <h3>7.3 Withdrawal from Subscriptions</h3>
      <p>
        Subscription fees are generally non-refundable. However, you may cancel your subscription at
        any time to prevent future renewals.
      </p>
      <p>
        AI outputs are generated without human review and may not reflect real-world accuracy or individual circumstances.
      </p>

      <h2>8. REFUNDS AND DISPUTES</h2>
      <h3>8.1 Refunds for Non-Performance</h3>
      <p>
        If you believe a Professional User did not fulfil or complete a service as intended, you may
        request a refund by contacting the Company at info@vivianlab.com.
      </p>
      <p>
        The Company will investigate and may issue a refund if it determines, based on documented
        evidence and after providing the Professional User with notice and an opportunity to
        respond, that the service was not fulfilled.
      </p>
      <p>
        The Company shall provide you with a concise explanation of the reasons for the refund
        decision upon request, unless prohibited by law or necessary for fraud prevention.
      </p>
      <h3>8.2 Technical Failures</h3>
      <p>
        If a technical problem prevents you or the Professional User from participating in a
        scheduled session (e.g., platform malfunction, connection failure), the Company may cancel
        the session at its discretion. In such cases:
      </p>
      <ul>
        <li>No charge will occur</li>
        <li>The session may be rescheduled free of charge</li>
      </ul>
      <p>
        Technical failures arising from the user’s device, internet connection, or software setup do
        not constitute grounds for automatic refund.This remains at the sole discretion of the
        Company.
      </p>

      <h2>9. PROHIBITED CONDUCT</h2>
      <p>Consumers must not:</p>
      <ul>
        <li>Create fake accounts or engage in fraudulent activity</li>
        <li>Misuse personal data of Professional Users or other Consumers</li>
        <li>Use offensive, threatening, or discriminatory language</li>
        <li>
          Harass Professional Users or other Consumers (harassment includes verbal or written
          expressions intended to cause mental or physical harm)
        </li>
        <li>Upload malware, viruses, or harmful software</li>
        <li>Copy, distribute, or reproduce Platform content without written permission</li>
        <li>Use the Company's trademarks, branding, or identity without consent</li>
        <li>
          Users must not misuse AI features, submit false or misleading symptom information, or
          attempt to generate medical diagnoses through the Platform.
        </li>
      </ul>
      <p>
        The main values governing the Platform are appreciation and respect. Discrimination based on
        gender, gender identity, nationality, religion, disability, sexual orientation, or age is
        strictly prohibited.
      </p>
      <p>If these rules are not followed, the Company may:</p>
      <ul>
        <li>Immediately disable your account</li>
        <li>Terminate the Agreement</li>
        <li>Contact law enforcement authorities</li>
        <li>Seek damages and other legal remedies</li>
      </ul>

      <h2>10. INTELLECTUAL PROPERTY RIGHTS</h2>
      <h3>10.1 Company’s Rights</h3>
      <p>
        All content, design, trademarks, logos, and software on the Platform are the exclusive
        property of the Company and are protected by copyright, trademark, and other intellectual
        property rights. AI-generated content may include automatically created text. Such content
        is owned or licensed by the Company and provided solely for personal, informational use.
      </p>
      <p>You may not:</p>
      <ul>
        <li>
          Copy, reproduce, distribute, or display Platform content without prior written consent
        </li>
        <li>
          Use the Company's trademark or branding to identify or promote third-party products or
          services
        </li>
        <li>Reverse-engineer, disassemble, or interfere with Platform software or systems</li>
      </ul>
      <p>
        Trademark protection: “VivianLab” is a registered trademark protected under Article 6a of
        the Paris Convention and Directive (EU) 2015/2436 of the European Parliament and of the
        Council of 16 December 2015 to approximate the laws of the Member States relating to trade
        marks.
      </p>
      <h3>10.2 User Content</h3>
      <p>
        By uploading content to your profile or the Platform (including photos, reviews, comments),
        you:
      </p>
      <ul>
        <li>Retain ownership of your content</li>
        <li>
          Grant the Company a non-exclusive, worldwide, royalty-free license to use, display, and
          distribute your content on the Platform for the purpose of providing the Services
        </li>
        <li>
          Warrant that you have all necessary rights to such content and that it does not infringe
          third-party rights
        </li>
      </ul>

      <h2>11. LIMITATION OF LIABILITY</h2>
      <h3>11.1 Company’s Limitation</h3>
      <p>
        The Platform is provided for informational and intermediary purposes only. It does not
        provide medical, health, or professional advice or services.
      </p>
      <p>To the fullest extent permitted by law:</p>
      <p>The Company is not liable for:</p>
      <ul>
        <li>The content, quality, or outcome of services provided by Professional Users</li>
        <li>Cancellations, no-shows, or unfulfilled appointments</li>
        <li>
          Any damages, losses, or injuries arising from Professional User services or interactions
        </li>
        <li>Loss of revenue, profits, contracts, or special, indirect, or consequential damages</li>
      </ul>
      <p>
        The Platform is provided "as is" without warranties of any kind (express or implied),
        including but not limited to warranties of merchantability, fitness for a particular
        purpose, or uninterrupted operation.
      </p>
      <p>
        However, this limitation of liability does not exclude or limit the Company's liability for:
      </p>
      <ul>
        <li>
          Death or personal injury resulting from the Company's own negligence or wilful misconduct
        </li>
        <li>Any mandatory statutory liability that cannot be excluded by law</li>
      </ul>
      <h3>11.2 Platform & AI Tools Disclaimer</h3>
      <p>
        The Platform, including all AI-assisted tools, wellness insights, symptom suggestions,
        educational content, and digital features, is provided solely for general informational and
        wellness purposes.
      </p>
      <p>
        AI-generated outputs may be inaccurate, incomplete, outdated, or inconsistent, and must not
        be relied upon for medical or clinical decision-making.
      </p>
      <p>
        The Platform and its AI tools do not diagnose, treat, prevent, or monitor any medical
        condition, including menopause-related symptoms.
      </p>
      <p>Consumers must consult licensed healthcare professionals for medical advice. AI systems may exhibit inherent limitations, including bias, hallucinations, or unpredictable outputs. The Company does not guarantee that AI outputs are free from errors or aligned with current medical standards. The AI features are not intended to diagnose, treat, cure, or prevent any disease under applicable U.S. law.</p>
      <h3>11.3 No Liability for AI Content or Wellness Guidance</h3>
      <p>
        To the fullest extent permitted by applicable law, the Company shall not be liable for any:
      </p>
      <ul>
        <li>
          Reliance by the Consumer on AI-generated content, wellness suggestions, or symptom
          interpretations
        </li>
        <li>Decisions taken based on AI outputs or educational content</li>
        <li>
          Physical, psychological, or emotional consequences resulting from the use of AI tools
        </li>
      </ul>
      <p>
        The Consumer remains solely responsible for evaluating and verifying all information
        provided via the Platform before taking any action. The Company shall not be liable for any damages arising from the inherent limitations of AI systems, including but not limited to errors, omissions, bias, or system unavailability.  This limitation of liability applies to the fullest extent permitted under applicable laws in all jurisdictions where the Platform is accessible, including the United States.
      </p>
      <h3>11.4 No Liability for Professional User Services</h3>
      <p>
        Professional Users operate as independent contractors, not employees, agents, or
        representatives of the Company.
      </p>
      <p>The Company does not guarantee, control, supervise, or verify:</p>
      <ul>
        <li>The accuracy or completeness of Professional User information</li>
        <li>Their qualifications, licensing, certifications, or competence</li>
        <li>The quality, outcome, or fulfilment of any service they provide</li>
      </ul>
      <p>
        The Company assumes no responsibility for cancellations, no-shows, dissatisfaction with
        services, or the consequences of advice provided by Professional Users.
      </p>
      <h3>11.5 Technical Issues</h3>
      <p>The Company is not liable for any:</p>
      <ul>
        <li>Service interruptions</li>
        <li>Delays or failures due to network issues or user device problems</li>
        <li>Loss of data</li>
        <li>Incompatibility with the Consumer’s hardware or software</li>
        <li>
          Third-party system failures (including hosting, video conferencing, or payment processors)
        </li>
      </ul>
      <h3>11.6 General Limitation</h3>
      <p>Except where prohibited by law, the Company shall not be liable for:</p>
      <ul>
        <li>Loss of profits or revenue</li>
        <li>Emotional distress</li>
        <li>Loss of data or goodwill</li>
        <li>Indirect, incidental, punitive, or consequential damages</li>
        <li>
          Any damages arising from misuse of the Platform, violations of Terms, or improper reliance
          on Platform content
        </li>
      </ul>
      <h3>11.7 Professional User Responsibility</h3>
      <p>
        Professional Users are independent contractors, not employees or agents of the Company.
        Professional Users are solely responsible for:
      </p>
      <ul>
        <li>The content, quality, and delivery of their services</li>
        <li>Their own professional licenses, insurance, and compliance with applicable laws</li>
      </ul>
      <p>
        The Company does not guarantee, endorse, or verify the qualifications, competence, or
        services of Professional Users.
      </p>

      <h2>12. FORCE MAJEURE</h2>
      <p>
        The Company cannot be held liable for failure to perform obligations if such failure results
        from events beyond its reasonable control, including but not limited to:
      </p>
      <ul>
        <li>Acts of God, natural disasters</li>
        <li>War, terrorism, civil unrest</li>
        <li>Labor strikes or shortages</li>
        <li>Disruption of communication networks</li>
        <li>Unavailability of payment processors</li>
        <li>Government actions or legal restrictions</li>
      </ul>
      <p>
        If a force majeure event occurs, the Company will notify affected Consumers within 15 days
        and provide an estimate of the expected duration. Disruptions to AI services or third-party
        APIs also constitute force majeure events.
      </p>

      <h2>13. TERMINATION AND SUSPENSION</h2>
      <h3>13.1 Termination by the Company</h3>
      <p>
        The Company may immediately suspend or terminate a Consumer’s access to the Platform,
        without prior notice, if:
      </p>
      <ul>
        <li>The Consumer violates these Terms of Use</li>
        <li>The Consumer engages in illegal, fraudulent, or harmful activity</li>
        <li>
          The Company receives a court order, government directive, or regulatory notice requiring
          suspension or termination
        </li>
        <li>
          Continued provision of services would expose the Company to legal or reputational risk
        </li>
      </ul>
      <p>
        The Company may also suspend or limit AI functionality where required to comply with regulatory obligations, risk mitigation measures, or safety concerns under the EU AI Act.
      </p>
      <p>
        Written notification will be provided after such action is taken. The Company may suspend or
        restrict access to AI Tools if necessary for safety, accuracy, or compliance with the EU AI
        Act.
      </p>
      <h3>13.2 Termination by Consumer</h3>
      <p>Consumers may terminate this Agreement at any time by:</p>
      <ul>
        <li>Closing their account via the Platform settings; or</li>
        <li>Sending written notice to the Company at</li>
      </ul>
      <p>
        Upon termination, the Company will delete or retain personal data in accordance with the{' '}
        <ExternalLink href="/privacy-policy">Privacy Policy</ExternalLink> and applicable law.
      </p>
      <h3>13.3 Effects of Termination</h3>
      <p>Upon termination:</p>
      <ul>
        <li>You will no longer have access to the Platform</li>
        <li>Any active subscriptions will be cancelled (no refund for unused portions)</li>
        <li>
          Data will be handled in accordance with the{' '}
          <ExternalLink href="/privacy-policy">Privacy Policy</ExternalLink>
        </li>
        <li>
          Any provisions intended to survive termination (including confidentiality and liability)
          will remain in effect
        </li>
        <li>
          AI-derived data may be retained in anonymized form for safety, audit, or model
          improvement, in compliance with GDPR.
        </li>
      </ul>

      <h2>14. MODIFICATION OF TERMS</h2>
      <p>
        The Company may modify these Terms of Use, the{' '}
        <ExternalLink href="/privacy-policy">Privacy Policy</ExternalLink>, and related policies at
        any time for various legal reasons, including security, intellectual property protection,
        regulatory compliance, or business reasons.
      </p>
      <p>
        If a modification substantially and negatively impacts your access to or use of the
        Platform, the Company will notify you in advance. You shall then have the right to terminate
        the Agreement within 30 days of the modification.
      </p>
      <p>
        Continued use of the Platform after modifications take effect constitutes acceptance of the
        modified terms.
      </p>
      <p>
        Consumers may save or retain a copy of these Terms at the time of registration. Significant
        changes affecting AI features, automated decision-making logic, or the use of special
        category data will be communicated to the Consumer in advance, as required by the EU AI Act
        and GDPR.
      </p>

      <h2>15. ASSIGNMENT OF RIGHTS</h2>
      <ul>
        <li>
          Consumers may not assign any rights or obligations under this Agreement without the
          Company’s prior written consent.
        </li>
        <li>
          The Company may assign its rights and obligations under this Agreement to any third party
          at its discretion (e.g., in connection with a merger, acquisition, or sale of business).
        </li>
      </ul>

      <h2>16. LAW AND JURISDICTION</h2>
      <h3>16.1 Governing Law</h3>
      <p>
        This Agreement is governed by Greek law, even when the Platform is accessed from outside
        Greece. Issues relating to AI transparency, bias, safety, or accountability shall be
        interpreted in accordance with applicable provisions of the EU AI Act (2024/2025 rollout),
        where relevant. Nothing in these Terms shall limit any mandatory consumer protection rights that may apply under the laws of the country in which the user resides.
      </p>
      <h3>16.2 Dispute Resolution</h3>
      <p>
        Any dispute arising out of or related to this Agreement shall first be subject to mediation
        in accordance with the Mediation Regulation of the Greek Ministry of Justice.
      </p>
      <p>
        If the dispute cannot be resolved through mediation, it shall be resolved exclusively by
        arbitration conducted in accordance with institutional arbitration rules, or by the Courts
        of Athens, which shall have exclusive jurisdiction.
      </p>
      <h3>16.3 Severability</h3>
      <p>
        If any provision of this Agreement is deemed invalid or unenforceable by a court, the
        remaining provisions shall remain in full force and effect.
      </p>

      <h2>17. ENTIRE AGREEMENT</h2>
      <p>
        These Terms of Use, together with the{' '}
        <ExternalLink href="/privacy-policy">Privacy Policy</ExternalLink>, constitute the entire
        agreement between the Consumer and the Company and supersede all prior agreements,
        understandings, or communications (whether written or oral) relating to the subject matter.
      </p>
      <p>
        No other terms, conditions, or representations shall be binding unless agreed to in writing
        by the Company.
      </p>

      <h2>18. CONTACT INFORMATION</h2>
      <p>For questions or concerns regarding these Terms of Use, please contact:</p>
      <p>
        Company: VIVIANLAB LTD
        <br />
        Address: 1612 SOFIA BUL. TSAR BORIS III № 81 ET.1, AP.1
        <br />
        Email: info@vivianlab.com
        <br />
        VAT: BG207933919
      </p>

      <h2>19. IMPORTANT NOTICE: RESPECT AND APPRECIATION</h2>
      <p>
        The Platform is governed by the values of respect and appreciation. All Consumers must
        observe these values in all interactions. Misuse of AI tools to generate abusive, harmful,
        or discriminatory content is strictly prohibited and may result in immediate account
        suspension.
      </p>
      <p>Zero tolerance for:</p>
      <ul>
        <li>Offensive, provocative, or threatening language</li>
        <li>
          Discrimination based on gender, gender identity, nationality, religion, disability, sexual
          orientation, or age
        </li>
        <li>Harassment of any kind</li>
      </ul>
      <p>Violations may result in account deactivation and/or legal action.</p>

      <h2>AI TRANSPARENCY STATEMENT</h2>
      <h3>AI Transparency Disclosure</h3>
      <p>
        The Platform includes AI-based features that generate automated content such as wellness
        suggestions, symptom interpretations, and personalized insights. When interacting with these
        features, you acknowledge that you are communicating with an artificial intelligence system,
        not a human. AI outputs may occasionally be incorrect or incomplete and must not be used as
        a basis for medical diagnosis, treatment, or urgent health decisions. Your use of AI
        features requires your explicit consent. You may disable AI functionality at any time by
        contacting the Company. AI systems used on the Platform do not perform automated decision-making that produces legal or similarly significant effects on users.
      </p>
      <p>
        Users have the right to request clarification regarding how AI-generated outputs are produced and how their data is used in AI processing by contacting the Company.
      </p>
      <p>THESE TERMS OF USE FOR CONSUMERS WERE LAST AMENDED ON 5/12/2025</p>
      <p>
        By clicking “I Accept” or by using the Platform, you acknowledge that you have read,
        understood, and agree to be bound by these Terms of Use and the{' '}
        <ExternalLink href="/privacy-policy">Privacy Policy</ExternalLink>.
      </p>
    </div>
  );
}

export default ConsumerTermsEnglish;
